import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiSearch,
  FiCheck,
  FiClock,
} from 'react-icons/fi';
import { isAuthenticated } from '../../lib/auth';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import styles from './Dashboard.module.css';

interface Order {
  id: string;
  orderNumber: string;
  customer: {
    email: string;
    name: string | null;
  };
  country: string;
  quantity: number;
  totalAmount: number;
  status: string;
  createdAt: string;
  withAccount: boolean;
  promoCode?: {
    code: string;
    discountPercent: number;
  } | null;
}

interface OrderStats {
  delivered: number;
  pending: number;
}

const Orders: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [orderStats, setOrderStats] = useState<OrderStats>({
    delivered: 0,
    pending: 0,
  });
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedCountry, setSelectedCountry] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchOrders = React.useCallback(async () => {
    try {
      const [ordersRes, statsRes] = await Promise.all([
        api.get('/admin/orders', {
          params: {
            status: selectedStatus !== 'all' ? selectedStatus : undefined,
            country: selectedCountry !== 'all' ? selectedCountry : undefined,
            search: searchTerm || undefined,
            page: currentPage,
            limit: 20,
          },
        }),
        api.get('/admin/stats'),
      ]);
      setOrders(ordersRes.data.orders || []);
      setTotalPages(ordersRes.data.pagination?.totalPages || 1);
      
      // Update order statistics
      if (statsRes.data.stats?.orders) {
        setOrderStats({
          delivered: statsRes.data.stats.orders.delivered || 0,
          pending: statsRes.data.stats.orders.pending || 0,
        });
      }
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    }
  }, [selectedStatus, selectedCountry, searchTerm, currentPage]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    fetchOrders();
  }, [navigate, fetchOrders]);

  return (
    <AdminLayout>
      <h1 style={{ fontSize: '1.375rem', marginBottom: '20px' }}>{t('admin.orders')}</h1>

        {/* Order Statistics */}
        <div className={styles.statsGrid} style={{ marginBottom: '20px' }}>
          <div 
            className={styles.statCard} 
            onClick={() => setSelectedStatus('DELIVERED')}
            style={{ cursor: 'pointer' }}
          >
            <div className={`${styles.statIcon} ${styles.green}`}>
              <FiCheck size={18} />
            </div>
            <div className={styles.statValue}>
              {orderStats.delivered}
            </div>
            <div className={styles.statLabel}>
              {t('admin.deliveredOrders' as string) || 'Delivered Orders'}
            </div>
          </div>
          <div 
            className={styles.statCard}
            onClick={() => setSelectedStatus('PENDING_PAYMENT')}
            style={{ cursor: 'pointer' }}
          >
            <div className={`${styles.statIcon} ${styles.yellow}`}>
              <FiClock size={18} />
            </div>
            <div className={styles.statValue}>{orderStats.pending}</div>
            <div className={styles.statLabel}>
              {t('admin.awaitingPayment' as string) || 'Awaiting Payment'}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
            <FiSearch
              size={18}
              style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#718096' }}
            />
            <input
              type="text"
              placeholder={t('admin.searchOrders' as string) || 'Search orders...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 40px',
                borderRadius: '8px',
                border: '1px solid #2d3748',
                background: '#1a202c',
                color: 'white',
              }}
            />
          </div>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #2d3748',
              background: '#1a202c',
              color: 'white',
            }}
          >
            <option value="all">{t('admin.allStatus' as string) || 'All Status'}</option>
            <option value="PENDING_PAYMENT">{t('orderStatus.PENDING_PAYMENT')}</option>
            <option value="DELIVERED">{t('orderStatus.DELIVERED')}</option>
            <option value="CANCELLED">{t('orderStatus.CANCELLED')}</option>
          </select>

          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #2d3748',
              background: '#1a202c',
              color: 'white',
            }}
          >
            <option value="all">{t('admin.allCountries' as string) || 'All Countries'}</option>
            <option value="FR">FR</option>
            <option value="US">US</option>
            <option value="ES">ES</option>
            <option value="OTHER">OTHER</option>
          </select>
        </div>

        {/* Orders table */}
        <div className={styles.tableSection}>
          <div className={styles.tableHeader}>
            <h2>{t('admin.orders')} ({orders.length})</h2>
          </div>
          {orders.length > 0 ? (
            <>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('orderStatus.orderNumber')}</th>
                    <th>{t('admin.customer' as string) || 'Customer'}</th>
                    <th>{t('checkout.country' as string) || 'Country'}</th>
                    <th>{t('checkout.quantity' as string) || 'Qty'}</th>
                    <th>{t('admin.promoCode' as string) || 'Promo'}</th>
                    <th>{t('checkout.total' as string) || 'Total'}</th>
                    <th>{t('orderStatus.status')}</th>
                    <th>{t('admin.date' as string) || 'Date'}</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td>
                        <Link
                          to={`/admin/orders/${order.id}`}
                          className={styles.orderLink}
                        >
                          {order.orderNumber}
                        </Link>
                      </td>
                      <td>
                        <div>{order.customer.email}</div>
                        {order.customer.name && (
                          <div style={{ fontSize: '12px', color: '#718096' }}>{order.customer.name}</div>
                        )}
                      </td>
                      <td>{order.country}</td>
                      <td>{order.quantity}</td>
                      <td>
                        {order.promoCode ? (
                          <span style={{ 
                            fontSize: '0.75rem', 
                            fontWeight: 600, 
                            color: 'var(--accent-text)',
                            background: 'var(--accent-dim)',
                            padding: '2px 8px',
                            borderRadius: '4px'
                          }}>
                            {order.promoCode.code}
                          </span>
                        ) : (
                          <span style={{ color: '#4a5568', fontSize: '0.875rem' }}>-</span>
                        )}
                      </td>
                      <td>${order.totalAmount.toFixed(2)}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${styles[order.status.toLowerCase()]}`}>
                          {t(`orderStatus.${order.status}`)}
                        </span>
                      </td>
                      <td>{new Date(order.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '20px' }}>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: '1px solid #2d3748',
                      background: currentPage === 1 ? '#1a202c' : '#2d3748',
                      color: currentPage === 1 ? '#4a5568' : 'white',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {t('admin.previous' as string) || 'Previous'}
                  </button>
                  <span style={{ padding: '8px 16px', color: '#a0aec0' }}>
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: '1px solid #2d3748',
                      background: currentPage === totalPages ? '#1a202c' : '#2d3748',
                      color: currentPage === totalPages ? '#4a5568' : 'white',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {t('admin.next' as string) || 'Next'}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className={styles.emptyState}>
              {t('admin.noOrders' as string) || 'No orders found'}
            </div>
          )}
        </div>
    </AdminLayout>
  );
};

export default Orders;
